/**
 * Wishlist JavaScript Module
 */

// Wishlist configuration (will be populated by WordPress localization)
let wishlistConfig = window.wishlist_ajax || {};

/**
 * Initialize wishlist buttons
 */
export function initWishlistButtons() {
    // Handle wishlist button clicks
    document.addEventListener('click', function(e) {
        if (e.target.closest('.wishlist-button')) {
            e.preventDefault();
            
            const button = e.target.closest('.wishlist-button');
            const productId = button.dataset.productId;
            const isInWishlist = button.dataset.inWishlist === '1';
            
            if (!productId) {
                return;
            }
            
            // Check if user is logged in
            if (!wishlistConfig.nonce) {
                showNotification(wishlistConfig.strings?.login_required || 'Vous devez être connecté pour utiliser la wishlist.', 'error');
                return;
            }
            
            if (isInWishlist) {
                removeFromWishlist(button, productId);
            } else {
                addToWishlist(button, productId);
            }
        }
    });
}

/**
 * Initialize wishlist page functionality
 */
export function initWishlistPage() {
    // Handle remove from wishlist on wishlist page
    document.addEventListener('click', function(e) {
        if (e.target.closest('.remove-from-wishlist-button')) {
            e.preventDefault();
            
            const button = e.target.closest('.remove-from-wishlist-button');
            const productId = button.dataset.productId;
            const wishlistItem = button.closest('.wishlist-item');
            
            if (!productId) {
                return;
            }
            
            if (confirm('Êtes-vous sûr de vouloir retirer ce produit de votre wishlist ?')) {
                removeFromWishlist(button, productId, function() {
                    if (wishlistItem) {
                        wishlistItem.remove();
                    }
                });
            }
        }
    });

    // Handle add to cart from wishlist
    document.addEventListener('click', function(e) {
        if (e.target.closest('.add-to-cart-button')) {
            e.preventDefault();
            
            const button = e.target.closest('.add-to-cart-button');
            const productId = button.dataset.productId;
            
            if (!productId) {
                return;
            }
            
            addToCartFromWishlist(button, productId);
        }
    });
}

/**
 * Add product to wishlist
 */
function addToWishlist(button, productId, callback = null) {
    const notes = ''; // Could be extended to capture notes
    
    // Set loading state
    setButtonLoading(button, true);
    
    fetch(wishlistConfig.ajax_url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            action: 'add_to_wishlist',
            product_id: productId,
            notes: notes,
            nonce: wishlistConfig.nonce
        })
    })
    .then(response => response.json())
    .then(data => {
        setButtonLoading(button, false);
        
        if (data.success) {
            // Update button state
            updateButtonState(button, true);
            
            // Update wishlist count
            updateWishlistCountDisplay(data.data?.wishlist_count || 0);
            
            // Show success message
            showNotification(
                wishlistConfig.strings?.success_add || 'Produit ajouté à votre wishlist !', 
                'success'
            );
            
            if (callback) callback();
        } else {
            showNotification(
                data.data?.message || wishlistConfig.strings?.error || 'Une erreur s\'est produite.', 
                'error'
            );
        }
    })
    .catch(() => {
        setButtonLoading(button, false);
        showNotification(wishlistConfig.strings?.error || 'Une erreur s\'est produite.', 'error');
    });
}

/**
 * Remove product from wishlist
 */
function removeFromWishlist(button, productId, callback = null) {
    // Set loading state
    setButtonLoading(button, true);
    
    fetch(wishlistConfig.ajax_url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            action: 'remove_from_wishlist',
            product_id: productId,
            nonce: wishlistConfig.nonce
        })
    })
    .then(response => response.json())
    .then(data => {
        setButtonLoading(button, false);
        
        if (data.success) {
            // Update button state
            updateButtonState(button, false);
            
            // Update wishlist count
            updateWishlistCountDisplay(data.data?.wishlist_count || 0);
            
            // Show success message
            showNotification(
                wishlistConfig.strings?.success_remove || 'Produit retiré de votre wishlist.', 
                'success'
            );
            
            if (callback) callback();
        } else {
            showNotification(
                data.data?.message || wishlistConfig.strings?.error || 'Une erreur s\'est produite.', 
                'error'
            );
        }
    })
    .catch(() => {
        setButtonLoading(button, false);
        showNotification(wishlistConfig.strings?.error || 'Une erreur s\'est produite.', 'error');
    });
}

/**
 * Add to cart from wishlist
 */
function addToCartFromWishlist(button, productId) {
    // Set loading state
    setButtonLoading(button, true);
    
    fetch(wishlistConfig.ajax_url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            action: 'add_to_cart_from_wishlist',
            product_id: productId,
            quantity: 1,
            nonce: wishlistConfig.nonce
        })
    })
    .then(response => response.json())
    .then(data => {
        setButtonLoading(button, false);
        
        if (data.success) {
            showNotification('Produit ajouté au panier !', 'success');
            
            // Optionally trigger cart update event
            document.dispatchEvent(new CustomEvent('wishlistAddedToCart', {
                detail: { productId, button }
            }));
        } else {
            showNotification(
                data.data?.message || 'Erreur lors de l\'ajout au panier.', 
                'error'
            );
        }
    })
    .catch(() => {
        setButtonLoading(button, false);
        showNotification('Erreur lors de l\'ajout au panier.', 'error');
    });
}

/**
 * Update wishlist count display
 */
export function updateWishlistCount() {
    if (!wishlistConfig.ajax_url || !wishlistConfig.nonce) {
        return;
    }
    
    fetch(wishlistConfig.ajax_url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            action: 'get_wishlist_count',
            nonce: wishlistConfig.nonce
        })
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) {
            updateWishlistCountDisplay(data.data?.count || 0);
        }
    })
    .catch(() => {
        console.warn('Failed to update wishlist count');
    });
}

/**
 * Update wishlist count in UI
 */
function updateWishlistCountDisplay(count) {
    const countElements = document.querySelectorAll('.wishlist-count');
    
    countElements.forEach(element => {
        element.textContent = count;
        
        // Hide/show count badge based on count
        if (count > 0) {
            element.style.display = '';
        } else {
            element.style.display = 'none';
        }
    });
}

/**
 * Set button loading state
 */
function setButtonLoading(button, loading) {
    if (loading) {
        button.classList.add('loading');
        button.disabled = true;
    } else {
        button.classList.remove('loading');
        button.disabled = false;
    }
}

/**
 * Update button state (in/out of wishlist)
 */
function updateButtonState(button, inWishlist) {
    const icon = button.querySelector('.wishlist-icon');
    const text = button.querySelector('.wishlist-text');
    
    if (inWishlist) {
        button.classList.add('in-wishlist');
        button.dataset.inWishlist = '1';
        if (icon) {
            icon.classList.remove('heart-empty');
            icon.classList.add('heart-filled');
        }
        if (text) {
            text.textContent = wishlistConfig.strings?.in_wishlist || 'Dans la wishlist';
        }
        button.title = wishlistConfig.strings?.remove_from_wishlist || 'Retirer de la wishlist';
    } else {
        button.classList.remove('in-wishlist');
        button.dataset.inWishlist = '0';
        if (icon) {
            icon.classList.remove('heart-filled');
            icon.classList.add('heart-empty');
        }
        if (text) {
            text.textContent = wishlistConfig.strings?.add_to_wishlist || 'Ajouter à la wishlist';
        }
        button.title = wishlistConfig.strings?.add_to_wishlist || 'Ajouter à la wishlist';
    }
}

/**
 * Show notification
 */
function showNotification(message, type) {
    const notification = document.createElement('div');
    notification.className = `wishlist-notification ${type}`;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    // Auto remove after 3 seconds
    setTimeout(() => {
        notification.style.opacity = '0';
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 300);
    }, 3000);
}

// Make functions globally available for backward compatibility
if (typeof window !== 'undefined') {
    window.WishlistManager = {
        addToWishlist: function(productId, callback) {
            const button = document.querySelector(`.wishlist-button[data-product-id="${productId}"]`);
            if (button) {
                addToWishlist(button, productId, callback);
            }
        },
        
        removeFromWishlist: function(productId, callback) {
            const button = document.querySelector(`.wishlist-button[data-product-id="${productId}"]`);
            if (button) {
                removeFromWishlist(button, productId, callback);
            }
        },
        
        updateCount: updateWishlistCount,
        
        isInWishlist: function(productId) {
            const button = document.querySelector(`.wishlist-button[data-product-id="${productId}"]`);
            return button && button.dataset.inWishlist === '1';
        }
    };
}
